import { useField } from '@unform/core';
import { ErrorMessage, TextField } from 'components';
import React, {
  ChangeEventHandler,
  ComponentProps,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { formatDateToUs } from 'utils/date';
import * as Styles from './styles';
interface IProps {
  name: string;
  notErrorMargin?: boolean;
  cleanValue?: boolean;
  parseDate?: boolean;
  parseToFloat?: boolean;
  hasError?: boolean;
  charLength?: number;
}

type InputProps = ComponentProps<typeof TextField> & IProps;

const TextInputComponent: React.FC<InputProps> = ({
  name,
  onChange,
  notErrorMargin,
  inputRef: propRef,
  cleanValue,
  parseDate,
  parseToFloat,
  hasError,
  charLength,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { fieldName, defaultValue, registerField, error, clearError } = useField(name);

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    e => {
      error && clearError();

      onChange?.(e);
    },
    [error, onChange],
  );

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef,
      getValue: ref => {
        const value = ref.current.value as string;

        if (cleanValue && value) {
          return value.replaceAll(/\D/g, '');
        }

        if (parseDate && value) {
          return formatDateToUs(value);
        }

        if (parseToFloat && value) {
          return parseFloat(value.replaceAll('.', '').replaceAll('R$ ', '').replaceAll(',', '.'));
        }

        return value;
      },
      setValue: (ref, value) => {
        ref.current.value = value;
      },
      clearValue: ref => {
        ref.current.value = '';
      },
    });
  }, [fieldName, registerField, cleanValue, parseToFloat]);

  useImperativeHandle(propRef, () => inputRef.current as HTMLInputElement, [inputRef, propRef]);

  return (
    <Styles.Container>
      <TextField
        {...rest}
        id={fieldName}
        inputRef={inputRef}
        onChange={handleChange}
        isInvalid={!!error || hasError}
        defaultValue={defaultValue}
        maxLength={charLength || 2000}
      />

      {notErrorMargin ? (
        error && <ErrorMessage>{error}</ErrorMessage>
      ) : (
        <ErrorMessage>{error}</ErrorMessage>
      )}
    </Styles.Container>
  );
};

export const TextInput = memo(TextInputComponent);
